import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { toast } from 'sonner'

export interface UseApiMutationOptions<TData, TError, TVars, TContext>
  extends Omit<UseMutationOptions<TData, TError, TVars, TContext>, 'mutationFn'> {
  successMessage?: string | ((data: TData, vars: TVars) => string)
  errorMessage?: string | ((error: TError, vars: TVars) => string)
  invalidateKeys?: QueryKey[]
  /** Skip toast notifications entirely (errors and successes). */
  silent?: boolean
}

export function useApiMutation<TData = unknown, TError = unknown, TVars = void, TContext = unknown>(
  mutationFn: (vars: TVars) => Promise<TData>,
  options: UseApiMutationOptions<TData, TError, TVars, TContext> = {},
) {
  const queryClient = useQueryClient()
  const {
    successMessage,
    errorMessage,
    invalidateKeys,
    silent,
    onSuccess,
    onError,
    ...rest
  } = options

  return useMutation<TData, TError, TVars, TContext>({
    mutationFn,
    ...rest,
    onSuccess: (data, vars, context) => {
      if (!silent && successMessage) {
        const msg =
          typeof successMessage === 'function' ? successMessage(data, vars) : successMessage
        if (msg) toast.success(msg)
      }
      if (invalidateKeys) {
        invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }))
      }
      onSuccess?.(data, vars, context)
    },
    onError: (error, vars, context) => {
      if (!silent) {
        const fallback = extractErrorMessage(error)
        const msg =
          typeof errorMessage === 'function'
            ? errorMessage(error, vars)
            : errorMessage ?? fallback
        if (msg) toast.error(msg)
      }
      onError?.(error, vars, context)
    },
  })
}

function errorText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || null
  }
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>
  return errorText(record.message)
    ?? errorText(record.detail)
    ?? errorText(record.error)
}

export function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as {
      response?: { data?: unknown }
      message?: unknown
    }
    return errorText(e.response?.data) ?? errorText(e.message) ?? 'Something went wrong'
  }
  return errorText(error) ?? 'Something went wrong'
}
